import moment from 'moment';
import { saveAs } from 'file-saver';
import { take } from 'rxjs/operators';
import { ChartOptions } from 'chart.js';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';

import {
  IMachineFromApi,
  MachineFunction,
  functonLabel,
  IOperatorFunctionAggregation,
  IOperatorRow,
  OperationsAggregationPeriod,
} from 'app/models';
import { OperatorFunctionAggregationService } from 'app/services';

// One entry per operator slot — deterministic by sorted operatorId position.
const OPERATOR_PALETTE = [
  { hex: '#e62e29', bg: 'rgba(230,46,41,0.75)',  bgLight: 'rgba(230,46,41,0.20)'  },
  { hex: '#3b82f6', bg: 'rgba(59,130,246,0.75)', bgLight: 'rgba(59,130,246,0.20)' },
  { hex: '#10b981', bg: 'rgba(16,185,129,0.75)', bgLight: 'rgba(16,185,129,0.20)' },
  { hex: '#f59e0b', bg: 'rgba(245,158,11,0.75)', bgLight: 'rgba(245,158,11,0.20)' },
  { hex: '#8b5cf6', bg: 'rgba(139,92,246,0.75)', bgLight: 'rgba(139,92,246,0.20)' },
  { hex: '#ec4899', bg: 'rgba(236,72,153,0.75)', bgLight: 'rgba(236,72,153,0.20)' },
  { hex: '#14b8a6', bg: 'rgba(20,184,166,0.75)', bgLight: 'rgba(20,184,166,0.20)' },
  { hex: '#f97316', bg: 'rgba(249,115,22,0.75)', bgLight: 'rgba(249,115,22,0.20)' },
];

@Component({
  selector: 'app-operations-component',
  templateUrl: './operations.component.html',
  styleUrls: ['./operations.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OperationsComponent implements OnInit {
  @Input() listOfMachinesFromApi: IMachineFromApi[] = [];

  selectedPeriod: OperationsAggregationPeriod = 'DAILY';
  selectedMachineFunction: string | null = null;
  selectedMachineId: number | null = null;
  sortBy: 'time' | 'name' = 'time';
  showBanner = true;
  isLoading = false;

  operatorRows: IOperatorRow[] = [];
  readonly machineFunctions = Object.values(MachineFunction);
  readonly functonLabel = functonLabel;
  readonly chartType = 'bar';

  // Stable reference — ng2-charts never triggers ngOnChanges on options.
  readonly chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      xAxes: [
        {
          gridLines: { color: 'rgba(0,0,0,0.04)' },
          ticks: { fontSize: 10, maxRotation: 45 },
        },
      ],
      yAxes: [
        {
          id: 'y-time',
          position: 'left',
          gridLines: { color: 'rgba(0,0,0,0.04)' },
          ticks: { min: 0, fontSize: 10 },
        },
        {
          id: 'y-jobs',
          position: 'right',
          gridLines: { drawOnChartArea: false },
          ticks: { min: 0, fontSize: 10 },
        },
      ],
    },
  };

  get filteredMachines(): IMachineFromApi[] {
    if (!this.selectedMachineFunction) { return this.listOfMachinesFromApi; }
    return this.listOfMachinesFromApi.filter(m => m.function === this.selectedMachineFunction);
  }

  get sortedRows(): IOperatorRow[] {
    return [...this.operatorRows].sort((a, b) =>
      this.sortBy === 'name'
        ? a.operatorName.localeCompare(b.operatorName)
        : b.totalWorkingTimeSecs - a.totalWorkingTimeSecs
    );
  }

  get periodLabel(): string {
    return { DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly' }[this.selectedPeriod];
  }

  get periodMeta(): string {
    return {
      DAILY: 'Last 14 completed days',
      WEEKLY: 'Last 12 completed weeks',
      MONTHLY: 'Last 12 completed months',
    }[this.selectedPeriod];
  }

  constructor(private readonly aggregationService: OperatorFunctionAggregationService) {}

  palette(row: IOperatorRow): typeof OPERATOR_PALETTE[0] {
    return OPERATOR_PALETTE[row.colorIndex % OPERATOR_PALETTE.length];
  }

  ngOnInit(): void {
    this.loadData();
  }

  onPeriodChange(period: OperationsAggregationPeriod): void {
    this.selectedPeriod = period;
    this.loadData();
  }

  onMachineFunctionSelect(fn: string | null): void {
    this.selectedMachineFunction = fn;
    this.selectedMachineId = null;
    this.loadData();
  }

  onMachineSelect(machineId: number | null): void {
    this.selectedMachineId = machineId;
    this.loadData();
  }

  fmtWorkingTime(secs: number): string {
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs % 3600) / 60);
    if (h === 0) { return `${m}m`; }
    if (m === 0) { return `${h}h`; }
    return `${h}h ${m}m`;
  }

  downloadCsv(): void {
    const lines: string[] = [
      'Operator Code,Operator Name,Period,Working Time (min),Jobs',
    ];
    this.sortedRows.forEach(row => {
      row.chartLabels.forEach((label, i) => {
        lines.push([
          row.operatorCode,
          `"${row.operatorName}"`,
          label,
          row.workingTimeData[i],
          row.jobsData[i],
        ].join(','));
      });
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const fn = this.selectedMachineFunction ? `_${this.selectedMachineFunction.toLowerCase()}` : '';
    saveAs(blob, `operations_${this.selectedPeriod.toLowerCase()}${fn}.csv`);
  }

  private loadData(): void {
    this.isLoading = true;
    this.aggregationService
      .getAggregations({
        type: this.selectedPeriod,
        machineFunction: this.selectedMachineFunction || undefined,
        machineId: this.selectedMachineId ?? undefined,
      })
      .pipe(take(1))
      .subscribe(
        (data) => {
          this.operatorRows = this.buildOperatorRows(data);
          this.isLoading = false;
        },
        () => { this.isLoading = false; }
      );
  }

  private buildOperatorRows(data: IOperatorFunctionAggregation[]): IOperatorRow[] {
    const byOperator = new Map<number, IOperatorFunctionAggregation[]>();
    data.forEach(item => {
      if (!byOperator.has(item.operatorId)) { byOperator.set(item.operatorId, []); }
      const bucket = byOperator.get(item.operatorId);
      if (bucket) { bucket.push(item); }
    });

    // Stable color mapping: sorted by operatorId so the same operator always
    // gets the same color regardless of which filter is active.
    const sortedIds = Array.from(byOperator.keys()).sort((a, b) => a - b);

    const rows: IOperatorRow[] = [];
    byOperator.forEach((entries, operatorId) => {
      const colorIndex = sortedIds.indexOf(operatorId);
      const sorted     = [...entries].sort((a, b) => a.periodStart.localeCompare(b.periodStart));
      const chartLabels     = sorted.map(e => this.formatPeriodLabel(e.periodStart));
      const workingTimeData = sorted.map(e => Math.round(e.workingTime / 60));
      const jobsData        = sorted.map(e => e.numberOfJobs);
      const totalWorkingTimeSecs = sorted.reduce((s, e) => s + e.workingTime, 0);
      const totalJobs            = sorted.reduce((s, e) => s + e.numberOfJobs, 0);

      // Fixed metric colors: red = working time (left axis), blue = jobs (right axis).
      // Stable dataset objects — built once per load cycle so ng2-charts
      // never sees a reference change between change-detection cycles.
      const chartDatasets = [
        {
          data: workingTimeData,
          label: 'Working time (min)',
          yAxisID: 'y-time',
          backgroundColor: 'rgba(230,46,41,0.75)',
          borderColor: '#e62e29',
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage: 0.85,
        },
        {
          data: jobsData,
          label: '# jobs',
          yAxisID: 'y-jobs',
          backgroundColor: 'rgba(59,130,246,0.55)',
          borderColor: '#3b82f6',
          borderWidth: 1,
          barPercentage: 0.7,
          categoryPercentage: 0.85,
        },
      ];

      rows.push({
        operatorId,
        operatorCode: entries[0].operatorCode,
        operatorName: entries[0].operatorName,
        totalWorkingTimeSecs,
        totalJobs,
        chartLabels,
        workingTimeData,
        jobsData,
        chartDatasets,
        colorIndex,
      });
    });

    return rows;
  }

  private formatPeriodLabel(periodStart: string): string {
    const m = moment(periodStart, 'YYYY-MM-DD HH:mm:ss');
    if (this.selectedPeriod === 'DAILY') { return m.format('MMM D'); }
    if (this.selectedPeriod === 'WEEKLY') { return m.format('[W]WW'); }
    return m.format('MMM YY');
  }
}
